import { useEffect, useRef, useState } from "react";

/**
 * Cross-origin image that needs an `Authorization` header (browsers can't put
 * headers on `<img src>`), rendered via a blob URL. Mints a FRESH Clerk token
 * per attempt (a captured token expires ~60s) and retries on transient failure.
 *
 * Blob hygiene (multi-minute overlays): the previous blob is revoked only AFTER
 * the new image decodes (`onLoad`), never synchronously on src swap — avoids a
 * broken-image flash. The effect cleanup aborts in-flight fetches and sweeps the
 * final blob, so a long session can't accumulate orphaned object URLs.
 */
export function AuthedImage({
  src,
  alt,
  getToken,
  className,
  placeholderClassName,
  placeholder,
}: {
  src: string;
  alt: string;
  getToken: () => Promise<string | null>;
  className?: string;
  placeholderClassName?: string;
  placeholder?: React.ReactNode;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [errStatus, setErrStatus] = useState<number | null>(null);
  // The blob URL currently shown by the <img> — revoked one swap behind.
  const shownRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    let pending: string | null = null; // created-but-not-yet-shown URL
    const delays = [0, 600, 1800];
    (async () => {
      for (let i = 0; i < delays.length; i++) {
        if (cancelled) return;
        if (delays[i]) await new Promise((r) => setTimeout(r, delays[i]));
        const token = await getToken();
        if (cancelled) return;
        if (!token) { setErrStatus(401); continue; }
        try {
          const res = await fetch(src, { headers: { authorization: `Bearer ${token}` }, signal: controller.signal });
          if (cancelled) return;
          if (res.ok) {
            const blob = await res.blob();
            if (cancelled) return;
            pending = URL.createObjectURL(blob);
            setBlobUrl(pending);
            setErrStatus(null);
            return;
          }
          setErrStatus(res.status);
        } catch {
          if (cancelled) return; // aborted or network error → next attempt
        }
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
      // Sweep any URL that was created for this src (shown or pending).
      if (pending && pending !== shownRef.current) URL.revokeObjectURL(pending);
      if (shownRef.current) { URL.revokeObjectURL(shownRef.current); shownRef.current = null; }
    };
  }, [src, getToken]);

  if (!blobUrl) {
    if (placeholder !== undefined) return <>{placeholder}</>;
    return (
      <div className={`${placeholderClassName ?? "aspect-[4/3]"} bg-stone-100 flex items-center justify-center text-stone-400 text-xs`}>
        {errStatus ? `image ${errStatus} — retrying…` : "loading…"}
      </div>
    );
  }
  return (
    <img
      src={blobUrl}
      alt={alt}
      decoding="async"
      className={className ?? "w-full aspect-[4/3] object-cover"}
      onLoad={() => {
        // The new blob has decoded — now safe to revoke the previous one.
        if (shownRef.current && shownRef.current !== blobUrl) URL.revokeObjectURL(shownRef.current);
        shownRef.current = blobUrl;
      }}
    />
  );
}
